import type { Metric as WebVitalsMetric } from 'web-vitals';
import * as webVitals from 'web-vitals';
import * as webapi from 'OK/webapi';
import debounce from 'OK/utils/debounce';

type WebVitalsRating = WebVitalsMetric['rating'];
type WebVitalsMetricName = WebVitalsMetric['name'];

type HtmlElementInfo = {
    tag: string,
    className: string,
}

type Report<Metric extends WebVitalsMetricName> = {
    htmlElementsInfo: HtmlElementInfo[],
    value: number,
    rating: WebVitalsRating,
    metric: Metric,
    url: string,
    state: string,
    isForAbMetrics: boolean,
}

type Reports<Metric extends WebVitalsMetricName = WebVitalsMetricName> = Report<Metric>[];

interface WebVitalsLoggerConfig {
    consoleLogsEnabled: boolean;
    isListenerEnabled: boolean;
    isConsoleClicksEnabled: boolean;
    inpReportsCapacity: number;
    clsReportsCapacity: number;
    isAutotestMetaDataSupported: boolean;
}

interface WebVitalsReportMetaInfo {
    hostname: string;
    artifactVersion: string;
    branchName: string;
    commitHash: string;
    isAutotest: boolean;
    retries: number;
}

interface WebVitalsData {
    reports: {
        cls: Reports;
        lcp: Reports;
        inp: Reports;
        fid: Reports;
    };
    meta: WebVitalsReportMetaInfo;
}

interface SendingInfo {
    reports: Reports;
    meta?: WebVitalsReportMetaInfo;
}

declare global {
    interface Window {
        webVitals: WebVitalsData;
    }
}

const STORAGE_KEY = 'webVitals';

const createDefaultWebVitalsData = (): WebVitalsData => ({
    reports: { cls: [], lcp: [], inp: [], fid: [] },
    meta: {
        hostname: '',
        artifactVersion: '',
        branchName: '',
        commitHash: '',
        isAutotest: false,
        retries: 0,
    },
});

const loadWebVitalsFromStorage = (): WebVitalsData => {
    const defaultData = createDefaultWebVitalsData();

    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return defaultData;
        }

        const parsed = JSON.parse(raw) as Partial<WebVitalsData>;

        return {
            reports: {
                cls: parsed.reports?.cls ?? [],
                lcp: parsed.reports?.lcp ?? [],
                inp: parsed.reports?.inp ?? [],
                fid: parsed.reports?.fid ?? [],
            },
            meta: { ...defaultData.meta, ...parsed.meta },
        };
    } catch (e) {
        console.warn('[WebVitals] Failed to load from sessionStorage', e);
        return defaultData;
    }
};

const saveWebVitalsToStorage = (data: WebVitalsData) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.warn('[WebVitals] Failed to save to sessionStorage: ', e);
    }
};

if (window.webVitals === undefined) {
    window.webVitals = createDefaultWebVitalsData();
}

const DEBOUNCE_DELAY = 1000;

/**
 * Репорт включает в себя несолько PerformanceEntry. Во всех случаях, кроме CLS, значение метрики берется из худшего PerformanceEntry,
 * который мы и хотим получить для более информативного отчета
 */
const getWorstEntry = <EntryType extends PerformanceEntry>(entries: EntryType[], metric: WebVitalsMetricName): EntryType => {
    const compareField = metric === 'LCP' ? 'renderTime' : 'duration';

    entries.sort((a, b) => compareEntries(a, b, compareField));

    return entries[0];
};

const compareEntries = (entryA: PerformanceEntry, entryB: PerformanceEntry, _fieldName: 'renderTime' | 'duration') => {
    const fieldName = _fieldName as keyof PerformanceEntry;

    if (entryA[fieldName] < entryB[fieldName]) {
        return 1;
    }

    if (entryA[fieldName] > entryB[fieldName]) {
        return -1;
    }

    return 0;
};

const createReport = <Metric extends WebVitalsMetricName>(
    htmlElementsInfo: HtmlElementInfo[] | null,
    value: number,
    rating: WebVitalsRating,
    metric: Metric
): Report<Metric> => ({
        htmlElementsInfo: htmlElementsInfo!,
        metric,
        value: parseFloat(value.toFixed(3)),
        url: window.location.pathname,
        state: OK.state.getCurrentStateId(),
        rating: rating,
        isForAbMetrics: true,
    });

const logReport = (report: Report<WebVitalsMetricName> | HTMLElement) => {
    const metricMap = {
        CLS: 'cls',
        LCP: 'lcp',
        INP: 'inp'
    } as const;

    if ('metric' in report) {
        const metricKey = metricMap[report.metric as keyof typeof metricMap];
        if (metricKey) {
            window.webVitals.reports[metricKey].push(report);
        }
        console.log(`[WebVitals] Report`, report);
    } else {
        console.log(`[WebVitals] Listener target`, report);
    }
};

class WebVitalsLogger {
    private reportsToSend: Reports = [];
    private worstClsReports: Reports<'CLS'> = [];
    private worstInpReports: Reports<'INP'> = [];

    private metaData: WebVitalsReportMetaInfo = createDefaultWebVitalsData().meta;

    private consoleLogsEnabled?: boolean;

    private isListenerEnabled?: boolean;
    private reportsCapacityMap?: Record<'CLS' | 'INP', number>;
    private listenerTarget: HTMLElement | null = null;
    public isConsoleClicksEnabled?: boolean;
    private isAutotestMetaDataSupported?: boolean;

    private eventHandler = (event: Event) => {
        this.listenerTarget = event.target as HTMLElement;
        if (this.isConsoleClicksEnabled) {
            logReport(this.listenerTarget);
        }
    }

    private addWorstReport(value: number,
                   rating: WebVitalsRating,
                   metric: 'CLS' | 'INP',
                   htmlElementsInfo: HtmlElementInfo[]) {
        const reportsList: Reports = metric === 'CLS' ? this.worstClsReports : this.worstInpReports;

        const result = createReport(htmlElementsInfo, value, rating, metric);

        const data = loadWebVitalsFromStorage();
        data.reports[metric.toLowerCase() as 'cls' | 'inp'].push(result);
        saveWebVitalsToStorage(data);

        if (this.consoleLogsEnabled) {
            logReport(result);
        }

        if (reportsList.length) {
            reportsList[reportsList.length - 1].isForAbMetrics = false; // Появился репорт хуже последнего
        }

        reportsList.push(result);

        if (reportsList.length > this.reportsCapacityMap?.[metric]!) {
            reportsList.shift();
        }
    }

    private initiateSendReports(reports: Reports) {
        if (!reports.length) {
            return;
        }

        this.reportsToSend.push(...reports);
        this.sendReports();
    }

    private sendReports = debounce(DEBOUNCE_DELAY, () => {
        const sendingInfo: SendingInfo = {reports: this.reportsToSend};

        if (this.isAutotestMetaDataSupported && this.metaData.isAutotest) {
            sendingInfo.meta = this.metaData;
            sendingInfo.meta.hostname = window.location.hostname;
        }

        webapi.invoke('web-vitals/send', sendingInfo);
        this.reportsToSend = [];
    });

    public activate(config: WebVitalsLoggerConfig) {
        this.consoleLogsEnabled = config.consoleLogsEnabled;
        this.isListenerEnabled = config.isListenerEnabled;
        this.isConsoleClicksEnabled = config.isConsoleClicksEnabled;
        this.isAutotestMetaDataSupported = config.isAutotestMetaDataSupported;

        this.metaData = loadWebVitalsFromStorage().meta;

        this.reportsCapacityMap = {
            INP: config.inpReportsCapacity,
            CLS: config.clsReportsCapacity,
        }

        /**
         * Колбек onReport вызывается один раз, когда пользователь впервые взаимодейтсвует со страницей.
         * Сам LCP репорт сохраняется в момент загрузки первой страницы, которую открыл пользователь
         */
        webVitals.onLCP(report => {
            const element = getWorstEntry(report.entries, report.name).element!;
            const className = getClassNameStr(element);

            const result = createReport(
                element ? [{tag: element.localName, className}] : null,
                report.value,
                report.rating,
                'LCP'
            );

            const data = loadWebVitalsFromStorage();
            data.reports.lcp.push(result);
            saveWebVitalsToStorage(data);

            if (this.consoleLogsEnabled) {
                logReport(result);
            }

            this.initiateSendReports([result]);
        });

        /**
         * При первом взаимодейтсвии со страницей вычисляется FID репорт и вызывается переданный колбек onReport.
         */
        webVitals.onFID(report => {
            const element = report.entries[0].target;
            const className = getClassNameStr(element);

            const result = createReport(
                // @ts-ignore
                element ? [{ tag: element.localName, className }] : null,
                report.value,
                report.rating,
                'FID'
            );

            const data = loadWebVitalsFromStorage();
            data.reports.fid.push(result);
            saveWebVitalsToStorage(data);

            if (this.consoleLogsEnabled) {
                logReport(result);
            }

            this.initiateSendReports([result]);
        });

        /**
         * Пердаем параметр reportAllChanges: true, поэтому колбек onReport будет вызываться каждый раз,
         * когда будет приходить CLS репорт с худшим значением, чем последний полученный репорт.
         */
        webVitals.onCLS(report => {
            if (!report.value) { // Первый CLS репорт часто приходит с нулевым значением. Такие репорты нас не интересуют
                return;
            }

            const addedElementsHashes = new Set();

            const htmlElementsInfo: HtmlElementInfo[] = report.entries.reduce((acc, entry) => {
                entry.sources.forEach(source => {
                    if (source.node) {
                        const { className, localName } = {
                            className: getClassNameStr(source.node),
                            // @ts-ignore
                            localName: source.node.localName
                        };

                        const elementHash = localName + className;

                        if (!addedElementsHashes.has(elementHash)) { // Фильтруем дубликаты элеметов, которые могут приходить в CLS отчетах
                            acc.push({ className, tag: localName });
                            addedElementsHashes.add(elementHash);
                        }
                    }
                });

                return acc;
            }, [] as HtmlElementInfo[]);

            this.addWorstReport(report.value, report.rating, 'CLS', htmlElementsInfo);
        }, {reportAllChanges: true});

        if (this.isListenerEnabled) {
            window.addEventListener('click', this.eventHandler, { capture: true });
            window.addEventListener('keydown', this.eventHandler, { capture: true });
        }
        /**
         * Пердаем параметр reportAllChanges: true, поэтому колбек onReport будет вызываться каждый раз,
         * когда будет приходить INP репорт с худшим значением, чем последний полученный репорт.
         */
        webVitals.onINP(report => {
            const element = getWorstEntry(report.entries, report.name).target;

            this.addWorstReport(
                report.value,
                report.rating,
                'INP',
                (() => {
                    if (element) {
                        const className = getClassNameStr(element);
                        // @ts-ignore
                        return [{ tag: element.localName, className }];
                    } else if (this.listenerTarget) {
                        const className = getClassNameStr(this.listenerTarget);
                        const localName = this.listenerTarget.localName;
                        this.listenerTarget = null;
                        return [{ tag: localName, className }];
                    }
                    return null;
                })() as HtmlElementInfo[]
            );
        }, {reportAllChanges: true});

        /**
         * Отправляем накопленные CLS и INP репорты.
         * При таком же условии вызывались бы переданные колбеки в onCLS и onINP при {reportAllChanges: false}
         */
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'hidden') {
                return;
            }

            const reports = [];

            if (this.worstClsReports.length) {
                reports.push(...this.worstClsReports);
                this.worstClsReports = [];
            }

            if (this.worstInpReports.length) {
                reports.push(...this.worstInpReports);
                this.worstInpReports = [];
            }

            this.initiateSendReports(reports);
        });
    }

    public deactivate() {
        if (this.isListenerEnabled) {
            window.removeEventListener('click', this.eventHandler, { capture: true });
            window.removeEventListener('keydown', this.eventHandler, { capture: true });
        }
    }

}

function getClassNameStr(element: Node | null): string {
    if (!element) {
        return '';
    }

    if (element instanceof SVGElement) {
        return element.className.baseVal;
    }

    if (element instanceof Element) {
        return element.className;
    }

    return '';
}

export const logger = new WebVitalsLogger();
